import React, { useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { CircularProgress } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
import CommonMultiSelectField from '../../../common/form/CommonMultiSelectField';
import CommonSelectField from '../../../common/form/CommonSelectField';
import { lighten } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { DATE_RANGE_SELECTED, getDateRangeRadioOptions } from '../ReportsUtils';
import { LAST_WEEK } from '../../../../utils/dates-const';
import { BACKGROUND_WHITE, BLACK_COLOR, DARK_BORDER_COLOR, LIGHT_GREY_COLOR } from '../../../../const/color-const';
import { STANDARD_INPUT_HEIGHT, STANDARD_INPUT_RADIUS, STANDARD_INPUT_WIDTH } from '../../../../const/chart-const';
import { fetchTopSalesReports } from '../../../../actions/report-actions';
import { bindActionCreators, compose } from 'redux';
import CommonButton from '../../../common/CommonButton';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

const styles = theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
  },
  formContent: {
    width: '100%',
    display: 'flex',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    '& div': {
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
      },
      '& .commonSelectField': {
        width: STANDARD_INPUT_WIDTH,
        backgroundColor: theme.palette.type === 'light' ? lighten(BACKGROUND_WHITE, 0.7) : BLACK_COLOR,
        maxHeight: 60,
        height: '100%',
      },
    },
    '& > div:nth-child(2)': {
      '& .commonSelectField': {
        marginRight: theme.spacing(2),
        marginLeft: 0,
      },
    },
  },
  datePickersWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    '& > div': {
      backgroundColor: theme.palette.type === 'light' ? lighten(BACKGROUND_WHITE, 0.7) : BLACK_COLOR,
      borderRadius: STANDARD_INPUT_RADIUS,
      height: STANDARD_INPUT_HEIGHT,
      width: STANDARD_INPUT_WIDTH,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: theme.palette.type === 'light' ? `1px solid ${LIGHT_GREY_COLOR}` : `1px solid ${DARK_BORDER_COLOR}`,
      '& p.Mui-error': {
        position: 'absolute',
        bottom: -10,
        left: 10,
      },
      '&:hover': {
        border: theme.palette.type === 'light' ? `1px solid ${BLACK_COLOR}` : `1px solid white`,
      },
      margin: theme.spacing(1, 2, 1, 0),
      '& label': {
        border: 'none',
        marginLeft: 10,
      },
      '& input': {
        paddingTop: 0,
        marginLeft: 10,
        border: 'none',
        height: '100%',
      },
      '& div': {
        '&::before': {
          display: 'none',
        },
        '&::after': {
          display: 'none',
        },
      },
    },
  },
  loadingBar: {
    marginTop: theme.spacing(2),
  },
});

const QUANTITY_OPTIONS = [5, 10, 20, 50, 100, 500, 1000, 5000, 10000, 20000, 30000].map(option => ({
  value: option,
  label: option,
}));



const TopSalesReportForm = React.forwardRef((props) => {
  const { reportName, machinesOptions, classes, fetchTopSalesReports, setFormValues } = props;
  const [dateRangeOption, setDateRangeOption] = useState(DATE_RANGE_SELECTED);

  
  return (
    <Formik
      initialValues={{
        machineIDs: [null],
        dateFrom: LAST_WEEK.option.dateFrom,
        dateTo: LAST_WEEK.option.dateTo,
        dateRange: LAST_WEEK.value,
        topSalesQuantity: 10,
      }}
      onSubmit={(values, formikHelpers) => {
        setFormValues(values);
        fetchTopSalesReports(reportName, machinesOptions.map(option => option.value), values, formikHelpers);
      }}>
      {({ isSubmitting, values, setFieldValue, isValid }) => {
        return (
          <Form className={classes.form}>
            <div className={classes.formContent}>
              <div>
                <Field
                  wide
                  atLeastOneElement
                  name="machineIDs"
                  label={
                    <FormattedMessage
                      id="field.cash.register"
                      defaultMessage="Cash Register"
                    />}
                  options={[...machinesOptions, {
                    label: <>- <FormattedMessage
                      id="all"
                      defaultMessage="All"
                    /> -</>, value: null,
                  }]}
                  component={CommonMultiSelectField}
                />
                <Field
                  wide
                  name="topSalesQuantity"
                  label={<FormattedMessage id="field.topsales.show.number" defaultMessage="Quantity options" />}
                  options={QUANTITY_OPTIONS}
                  component={CommonSelectField}
                />
              </div>
              {getDateRangeRadioOptions(setDateRangeOption, values, setFieldValue, dateRangeOption, classes)}
            </div>
      
            <CommonButton
              isAnimated
              size="large"
              type="submit"
              label={<><FormattedMessage
                id="search"
                defaultMessage="Search" /></>}
              disabled={isSubmitting || !values.machineIDs || !values.dateFrom || !values.dateTo || !isValid} />
            
            {isSubmitting && <div className={classes.loadingBar}>
              <CircularProgress color="primary" />
            </div>}
          </Form>
        );
      }}
    </Formik>
  );
});

TopSalesReportForm.propTypes = {
  reportName: PropTypes.string,
  machinesOptions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  classes: PropTypes.object,
  fetchTopSalesReports: PropTypes.func,
  setFormValues: PropTypes.func,
};

const decorate = compose(
  withStyles(styles),
  connect(
    store => ({
      machines: store.user.machines,
      reports: store.report.standard,
    }),
    dispatch => bindActionCreators({
      fetchTopSalesReports,
    }, dispatch)),
);

export default decorate(TopSalesReportForm);
